import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import NavLinks from "@/components/navigation/NavLinks";
import { useFocusTrap, useKeyboardNavigation } from "@/utils/accessibility";
import { ThemeToggle } from "@/components/ui/theme-toggle";

interface NavbarProps {
  isBannerVisible?: boolean;
}

const Navbar = ({ isBannerVisible = false }: NavbarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  
  const mobileMenuRef = useFocusTrap(isMobileMenuOpen);
  const { onKeyDown: handleMenuKeyDown } = useKeyboardNavigation(() => 
    setIsMobileMenuOpen(!isMobileMenuOpen)
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const topPosition = isBannerVisible ? '60px' : '0px';
  const navHeight = isScrolled ? '64px' : '80px';

  return (
    <nav 
      id="navigation"
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? "bg-solr-space-darker/95 border-b border-solr-nebula-purple/10" : ""
      }`}
      style={{ 
        top: topPosition,
        height: navHeight
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto h-full px-4 md:px-6 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <h1 className="font-display text-3xl lowercase tracking-wider text-white">solr</h1>
        </Link>

        <div className="hidden md:flex items-center space-x-8">
          <NavLinks />
        </div>

        <div className="hidden md:flex items-center space-x-3">
          <ThemeToggle />
          <Link
            to="/cart"
            className="relative inline-flex items-center gap-2 rounded-full border-[1.5px] border-[hsl(var(--magenta)/0.6)] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white hover:bg-[hsl(var(--magenta)/0.12)] hover:border-[hsl(var(--magenta))] transition-colors focus:outline-none focus:ring-2 focus:ring-[hsl(var(--magenta))]"
            aria-label={`Shopping cart${cartCount > 0 ? ` with ${cartCount} item${cartCount === 1 ? '' : 's'}` : ', empty'}`}
          >
            <ShoppingCart className="w-4 h-4" />
            <span>Cart ({cartCount})</span>
          </Link>
        </div>

        <div className="flex md:hidden items-center space-x-4">
          <ThemeToggle />
          <Link
            to="/cart" 
            className="relative p-2 rounded-full hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-solr-nebula-purple focus:ring-offset-2 focus:ring-offset-transparent min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={`Shopping cart${cartCount > 0 ? ` with ${cartCount} item${cartCount === 1 ? '' : 's'}` : ', empty'}`}
          >
            <ShoppingCart className="text-solr-starfield-white w-5 h-5" />
            {cartCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 bg-solr-nebula-purple text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                aria-hidden="true"
              >
                {cartCount}
              </span>
            )}
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            onKeyDown={handleMenuKeyDown}
            className="p-2 rounded-md hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-solr-nebula-purple focus:ring-offset-2 focus:ring-offset-transparent min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={`${isMobileMenuOpen ? 'Close' : 'Open'} mobile menu`}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            data-close-on-escape={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="text-solr-starfield-white w-6 h-6" />
            ) : (
              <Menu className="text-solr-starfield-white w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          id="mobile-menu"
          className="md:hidden bg-solr-space-darker/95 border-b border-solr-nebula-purple/10 fixed left-0 right-0 z-30"
          style={{ top: `calc(${topPosition} + ${navHeight})` }}
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div className="py-4 px-4 space-y-4">
            <NavLinks />
            <Link to="/shop">
              <Button 
                className="btn-primary w-full min-h-[44px]" 
                onClick={() => setIsMobileMenuOpen(false)}
                role="menuitem"
              >
                Shop Now
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
